/**
 * Text rendering utilities for SheetsViewerV2
 *
 * This module provides PDF-space (world-space) text rendering using
 * opentype.js to convert text to SVG paths for vector rendering.
 */

import opentype from 'opentype.js';
import Edit2D = Autodesk.Extensions.Edit2D;
import { ChakraColor } from 'src/utils/color-util';

// Import font file - Vite will provide the correct URL
import nunitoRegularUrl from 'src/assets/Nunito-Regular.ttf?url';

export interface TextRenderOptions {
  fontSize?: number;
  color?: string;
}

const DEFAULT_OPTIONS: Required<TextRenderOptions> = {
  fontSize: 0.1, // In world units
  color: ChakraColor.gray700,
};

// Font cache to avoid reloading
let cachedFont: opentype.Font | null = null;
let fontLoadPromise: Promise<opentype.Font> | null = null;

/**
 * Load the Nunito font from local assets
 */
async function loadFont(): Promise<opentype.Font> {
  if (cachedFont) {
    return cachedFont;
  }

  if (fontLoadPromise) {
    return fontLoadPromise;
  }

  fontLoadPromise = new Promise((resolve, reject) => {
    // Use the Vite-imported font URL
    opentype.load(nunitoRegularUrl, (err, font) => {
      if (err || !font) {
        console.error('[text-rendering] Failed to load Nunito font:', err);
        reject(new Error('Failed to load Nunito font'));
        return;
      }
      cachedFont = font;
      resolve(font);
    });
  });

  return fontLoadPromise;
}

/**
 * Check if a number is valid (not NaN, not Infinity)
 */
function isValidNumber(n: number): boolean {
  return Number.isFinite(n);
}

/**
 * Format a number for SVG path, with validation
 */
function formatCoord(n: number): string {
  if (!isValidNumber(n)) {
    console.warn('[text-rendering] Invalid coordinate:', n);
    return '0';
  }
  return n.toFixed(4);
}

/**
 * Approximate a quadratic bezier curve with line segments
 * @param x0 Start x
 * @param y0 Start y
 * @param x1 Control point x
 * @param y1 Control point y
 * @param x2 End x
 * @param y2 End y
 * @param segments Number of line segments to use
 */
function approximateQuadraticBezier(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  segments = 2 // Reduced from 4 for performance
): string {
  let path = '';
  for (let i = 1; i <= segments; i++) {
    const t = i / segments;
    const mt = 1 - t;
    // Quadratic bezier formula: B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
    const px = mt * mt * x0 + 2 * mt * t * x1 + t * t * x2;
    const py = mt * mt * y0 + 2 * mt * t * y1 + t * t * y2;
    path += `L ${formatCoord(px)},${formatCoord(py)} `;
  }
  return path;
}

/**
 * Approximate a cubic bezier curve with line segments
 */
function approximateCubicBezier(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  segments = 3 // Reduced from 6 for performance
): string {
  let path = '';
  for (let i = 1; i <= segments; i++) {
    const t = i / segments;
    const mt = 1 - t;
    // Cubic bezier formula
    const px =
      mt * mt * mt * x0 +
      3 * mt * mt * t * x1 +
      3 * mt * t * t * x2 +
      t * t * t * x3;
    const py =
      mt * mt * mt * y0 +
      3 * mt * mt * t * y1 +
      3 * mt * t * t * y2 +
      t * t * t * y3;
    path += `L ${formatCoord(px)},${formatCoord(py)} `;
  }
  return path;
}

/**
 * Convert text to an SVG path data string (just the d attribute content)
 * Flips y-axis since OpenType uses y-down but our viewer uses y-up
 * Converts bezier curves to line segments since Edit2D.fromSVG only supports M, L, Z
 */
function textToSvgPathData(
  font: opentype.Font,
  text: string,
  x: number,
  y: number,
  fontSize: number
): string {
  // Get the path at origin first, then we'll transform it
  const path = font.getPath(text, 0, 0, fontSize);

  // Build path data manually with y-axis flip
  // Convert bezier curves to line segments since fromSVG only supports M, L, Z
  let pathData = '';
  let currentX = x; // Start at the position
  let currentY = y;

  for (const cmd of path.commands) {
    switch (cmd.type) {
      case 'M':
        currentX = x + cmd.x;
        currentY = y - cmd.y;
        pathData += `M ${formatCoord(currentX)},${formatCoord(currentY)} `;
        break;
      case 'L':
        currentX = x + cmd.x;
        currentY = y - cmd.y;
        pathData += `L ${formatCoord(currentX)},${formatCoord(currentY)} `;
        break;
      case 'C':
        // Cubic bezier - approximate with line segments
        pathData += approximateCubicBezier(
          currentX,
          currentY,
          x + cmd.x1,
          y - cmd.y1,
          x + cmd.x2,
          y - cmd.y2,
          x + cmd.x,
          y - cmd.y
        );
        currentX = x + cmd.x;
        currentY = y - cmd.y;
        break;
      case 'Q':
        // Quadratic bezier - approximate with line segments
        pathData += approximateQuadraticBezier(
          currentX,
          currentY,
          x + cmd.x1,
          y - cmd.y1,
          x + cmd.x,
          y - cmd.y
        );
        currentX = x + cmd.x;
        currentY = y - cmd.y;
        break;
      case 'Z':
        pathData += 'Z ';
        break;
    }
  }

  return pathData.trim();
}

/**
 * PDF-space text shape that renders as vector graphics
 */
export class PdfSpaceTextShape {
  private shapes: Autodesk.Edit2D.Shape[] = [];
  private edit2d: Edit2D;

  constructor(edit2d: Edit2D) {
    this.edit2d = edit2d;
  }

  /**
   * Create text shape at the given position
   * Note: This is async because font loading may be required
   */
  async createText(
    text: string,
    position: { x: number; y: number },
    options: TextRenderOptions = {}
  ): Promise<Autodesk.Edit2D.Shape | null> {
    if (!text) return null;

    const opts = { ...DEFAULT_OPTIONS, ...options };

    try {
      const font = await loadFont();

      // Get SVG path data for the text (with y-axis flipped for viewer coordinates)
      const pathData = textToSvgPathData(
        font,
        text,
        position.x,
        position.y,
        opts.fontSize
      );

      if (!pathData) {
        console.warn('[text-rendering] Empty path data for text:', text);
        return null;
      }

      // Create SVG path element
      const svg = `<path d="${pathData}"/>`;

      // Create Edit2D shape from SVG
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const shape = Autodesk.Edit2D.Shape.fromSVG(svg);

      if (shape) {
        shape.style = {
          fillColor: opts.color,
          fillAlpha: 1,
          lineColor: 'transparent',
          lineAlpha: 0,
          lineWidth: 0,
        };
        this.shapes.push(shape);
      } else {
        console.warn(
          '[text-rendering] Failed to create shape from SVG for text:',
          text
        );
      }

      return shape;
    } catch (error) {
      console.warn('[text-rendering] Failed to create text shape:', error);
      return null;
    }
  }

  /**
   * Add all shapes to the Edit2D layer
   */
  draw(): void {
    this.shapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.addShape(shape);
    });
    this.edit2d.defaultContext.layer.update();
  }

  /**
   * Remove all shapes from the Edit2D layer
   */
  remove(): void {
    this.shapes.forEach((shape) => {
      this.edit2d.defaultContext.layer.removeShape(shape);
    });
    this.edit2d.defaultContext.layer.update();
    this.shapes = [];
  }

  /**
   * Get all created shapes
   */
  getShapes(): Autodesk.Edit2D.Shape[] {
    return this.shapes;
  }
}

/**
 * Helper to create multiple text labels in PDF space
 */
export class PdfSpaceTextRenderer {
  private edit2d: Edit2D;
  private textShapes: Map<string, PdfSpaceTextShape> = new Map();
  private batchedShape: Autodesk.Edit2D.Shape | null = null;

  constructor(edit2d: Edit2D) {
    this.edit2d = edit2d;
  }

  /**
   * Add a text label at the given position
   */
  async addText(
    id: string,
    text: string,
    position: { x: number; y: number },
    options: TextRenderOptions = {}
  ): Promise<void> {
    // Remove existing text with same ID
    this.removeText(id);

    const textShape = new PdfSpaceTextShape(this.edit2d);
    await textShape.createText(text, position, options);
    textShape.draw();

    this.textShapes.set(id, textShape);
  }

  /**
   * Add multiple text labels as a single batched shape (more performant)
   * All text will share the same color/style from the first entry's options
   */
  async addBatchedText(
    entries: Array<{
      id: string;
      text: string;
      position: { x: number; y: number };
    }>,
    options: TextRenderOptions = {}
  ): Promise<void> {
    // Clear any existing batched shape
    this.clearBatched();

    if (entries.length === 0) return;

    const opts = { ...DEFAULT_OPTIONS, ...options };

    try {
      const font = await loadFont();

      // Combine all text paths into a single path data string
      let combinedPathData = '';

      for (const entry of entries) {
        if (!entry.text) continue;

        const pathData = textToSvgPathData(
          font,
          entry.text,
          entry.position.x,
          entry.position.y,
          opts.fontSize
        );

        if (pathData) {
          combinedPathData += pathData + ' ';
        }
      }

      if (!combinedPathData) return;

      // Create a single SVG path element for all text
      const svg = `<path d="${combinedPathData.trim()}"/>`;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const shape = Autodesk.Edit2D.Shape.fromSVG(svg);

      if (shape) {
        shape.style.fillColor = opts.color;
        shape.style.fillAlpha = 1;
        shape.style.lineColor = 'transparent';
        shape.style.lineAlpha = 0;
        shape.style.lineWidth = 0;

        this.edit2d.defaultContext.layer.addShape(shape);
        this.edit2d.defaultContext.layer.update();
        this.batchedShape = shape;
      }
    } catch (error) {
      console.warn('[text-rendering] Failed to create batched text:', error);
    }
  }

  /**
   * Clear the batched shape
   */
  clearBatched(): void {
    if (this.batchedShape) {
      this.edit2d.defaultContext.layer.removeShape(this.batchedShape);
      this.edit2d.defaultContext.layer.update();
      this.batchedShape = null;
    }
  }

  /**
   * Remove a text label by ID
   */
  removeText(id: string): void {
    const textShape = this.textShapes.get(id);
    if (textShape) {
      textShape.remove();
      this.textShapes.delete(id);
    }
  }

  /**
   * Remove all text labels
   */
  clear(): void {
    for (const textShape of this.textShapes.values()) {
      textShape.remove();
    }
    this.textShapes.clear();
    this.clearBatched();
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    this.clear();
  }
}

/**
 * Preload the font to avoid delays when rendering text
 */
export async function preloadFont(): Promise<void> {
  await loadFont();
}
